import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plug, CheckCircle2, AlertCircle, X, Power, Search } from "lucide-react";
import api from "@/lib/api";
import { GlassCard, SectionLabel, GradientButton, GhostButton } from "@/components/primitives";

const FIELDS = {
  pagerduty: [
    { name: "api_token", label: "API Token", placeholder: "u+EXAMPLE_TOKEN", secret: true },
    { name: "service_id", label: "Default Service ID", placeholder: "PXXXXX" },
  ],
  slack: [
    { name: "webhook_url", label: "Incoming Webhook URL", placeholder: "https://hooks.slack.com/services/...", secret: true },
    { name: "channel", label: "Default Channel", placeholder: "#incidents" },
  ],
  jira: [
    { name: "domain", label: "Jira Domain", placeholder: "yourco.atlassian.net" },
    { name: "email", label: "Email" },
    { name: "api_token", label: "API Token", secret: true },
    { name: "project_key", label: "Project Key", placeholder: "OPS" },
  ],
  github: [
    { name: "personal_access_token", label: "Personal Access Token", placeholder: "ghp_...", secret: true },
    { name: "default_repo", label: "Default Repo", placeholder: "yourorg/yourrepo" },
  ],
  datadog: [
    { name: "api_key", label: "API Key", secret: true },
    { name: "app_key", label: "Application Key", secret: true },
    { name: "site", label: "Site", placeholder: "datadoghq.com" },
  ],
  servicenow: [
    { name: "instance", label: "Instance", placeholder: "yourco.service-now.com" },
    { name: "username", label: "Username" },
    { name: "password", label: "Password", secret: true },
  ],
};

const STATUS_COLORS = {
  connected: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  disconnected: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  error: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

export default function Connectors() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [testResults, setTestResults] = useState({});

  const load = async () => { const { data } = await api.get("/connectors"); setList(data); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => list.filter((c) => !q || (c.name + c.category + c.description).toLowerCase().includes(q.toLowerCase())), [list, q]);

  const onTest = async (slug) => {
    setTestResults((r) => ({ ...r, [slug]: { state: "running" } }));
    try {
      const { data } = await api.post(`/connectors/${slug}/test`);
      setTestResults((r) => ({ ...r, [slug]: { state: "ok", ...data } }));
    } catch (e) {
      setTestResults((r) => ({ ...r, [slug]: { state: "fail", error: e?.response?.data?.detail || "Failed" } }));
    }
  };

  const onDisconnect = async (slug) => {
    await api.post(`/connectors/${slug}/disconnect`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="connectors-page">
      <div>
        <SectionLabel>Integrations</SectionLabel>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Connect your <span className="gradient-text">operations stack</span></h1>
        <p className="text-sm text-slate-400 mt-1 max-w-xl">Bring SentinelAI into your existing tools — sync incidents, notify on-call and link tickets bidirectionally.</p>
      </div>

      <GlassCard className="p-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <input data-testid="connectors-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search integrations" className="w-full pl-9 pr-3 py-2 bg-white/[0.04] border border-white/10 rounded-xl text-sm focus:outline-none focus:border-cyan-400/50" />
        </div>
      </GlassCard>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c, i) => {
          const tr = testResults[c.slug];
          return (
            <motion.div key={c.slug} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <GlassCard className="p-5 h-full flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl grid place-items-center text-white font-bold" style={{ background: `linear-gradient(135deg, ${c.color}, #1E293B)` }}>{c.name[0]}</div>
                    <div>
                      <div className="text-sm font-semibold">{c.name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{c.category}</div>
                    </div>
                  </div>
                  <span data-testid={`connector-status-${c.slug}`} className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_COLORS[c.status] || STATUS_COLORS.disconnected}`}>{c.status}</span>
                </div>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.description}</p>
                <div className="mt-auto pt-4 flex items-center gap-2">
                  {c.status === "connected" ? (
                    <>
                      <GhostButton onClick={() => onTest(c.slug)} data-testid={`connector-test-${c.slug}`}>Test</GhostButton>
                      <GhostButton onClick={() => onDisconnect(c.slug)} data-testid={`connector-disconnect-${c.slug}`}><Power className="h-3.5 w-3.5" /> Disconnect</GhostButton>
                      <GradientButton onClick={() => setActive(c)} data-testid={`connector-configure-${c.slug}`}>Configure</GradientButton>
                    </>
                  ) : (
                    <GradientButton onClick={() => setActive(c)} data-testid={`connector-connect-${c.slug}`}><Plug className="h-3.5 w-3.5" /> Connect</GradientButton>
                  )}
                </div>
                {tr && (
                  <div className={`mt-3 text-xs font-mono ${tr.state === "ok" ? "text-emerald-300" : tr.state === "fail" ? "text-rose-300" : "text-slate-400"}`}>
                    {tr.state === "running" && "Testing connection…"}
                    {tr.state === "ok" && <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> OK · {tr.latency_ms}ms</span>}
                    {tr.state === "fail" && <span className="inline-flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> {tr.error}</span>}
                  </div>
                )}
              </GlassCard>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {active && <ConnectorDialog connector={active} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} />}
      </AnimatePresence>
    </div>
  );
}

function ConnectorDialog({ connector, onClose, onSaved }) {
  const fields = FIELDS[connector.slug] || [];
  const [data, setData] = useState(() => Object.fromEntries(fields.map((f) => [f.name, connector.config?.[f.name] || ""])));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post(`/connectors/${connector.slug}/connect`, data);
      onSaved();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4" onClick={onClose}>
      <motion.form initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }} onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-lg glass p-6" data-testid={`connector-dialog-${connector.slug}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl grid place-items-center text-white font-bold" style={{ background: `linear-gradient(135deg, ${connector.color}, #1E293B)` }}>{connector.name[0]}</div>
            <div>
              <div className="text-base font-semibold">Connect {connector.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{connector.category}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-5 space-y-3">
          {fields.map((f) => (
            <label key={f.name} className="block">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{f.label}{f.secret && " (secret)"}</div>
              <input
                data-testid={`connector-field-${connector.slug}-${f.name}`}
                type={f.secret ? "password" : "text"} required value={data[f.name] || ""} onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
                placeholder={f.placeholder || ""}
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-400/50"
              />
            </label>
          ))}
        </div>
        {err && <div className="mt-3 text-sm text-rose-300">{err}</div>}
        <div className="mt-6 flex justify-end gap-2">
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <GradientButton type="submit" disabled={busy} data-testid={`connector-save-${connector.slug}`}>{busy ? "Saving…" : "Save & Connect"}</GradientButton>
        </div>
        <div className="mt-4 text-[10px] font-mono text-slate-500">Secrets are masked at rest. SentinelAI never exposes plain credentials in logs or UI.</div>
      </motion.form>
    </motion.div>
  );
}
